import _ from 'lodash'
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query'

import { Consultation, ConsultationReason, CreateConsultationDto, UpdateBillingReviewDto, UpdateConsultationDto, PaymentType } from '@/core/models/consultation.model'
import { CreateDocumentDto, Document } from '@/core/models/document.model'
import { SearchRequest, SearchRequestOptions } from '@/core/types/request'
import { SocketEvent } from '@/core/types/socket-event'
import { WithId } from '@/core/types/shared'

import { cacheByIdArg } from '@/utils/rtkQueryCacheUtils'

import { baseApi, Tag } from './base'
import { fetchPresignedUrl, uploadToAWS } from './file'
import { getSocket } from './socket'
import { updateConsultationSuccess } from '@/redux/reducers/consultation.reducer'

type CreateConsultationParams = CreateConsultationDto

type CreateBiologicalParams = {
  label: string
  comment?: string
  unit: string
  value: string
  file?: string
}

type RetryDesmosActsParams = {
  networkId: string
  waitingRoomId: string
  consultationId: string
}

type AddManualDesmosActParams = {
  consultationId: string
  actData: any
}

export const consultationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getConsultation: builder.query<Consultation, string>({
      query: (consultationId) => `/consultations/${consultationId}`,
      async onCacheEntryAdded(consultationId, { updateCachedData, cacheDataLoaded, cacheEntryRemoved, dispatch }) {
        const socket = await getSocket()
        const consultationUpdatedListener = (updatedConsultation: Consultation) => {
          updateCachedData((cachedConsultation) => {
            if (cachedConsultation._id === updatedConsultation._id) {
              // Object.assign does a shallow merge, which means a partial staff
              // sub-document (e.g. medecin without userInfo, or a bare string ID)
              // would silently overwrite the fully-populated version already in the
              // cache. Preserve the cached sub-document whenever the update would
              // lose userInfo.
              const mergeStaffField = (updated: any, cached: any) => {
                // Socket sent a bare string ID (non-populated ref) — keep cached
                if (!updated || typeof updated !== 'object') {
                  return cached ?? updated
                }
                // Socket sent an object but without userInfo — graft cached userInfo in
                if (!updated.userInfo && cached?.userInfo) {
                  return { ...updated, userInfo: cached.userInfo }
                }
                return updated
              }
              Object.assign(cachedConsultation, updatedConsultation, {
                medecin: mergeStaffField(updatedConsultation.medecin, cachedConsultation.medecin),
                pds: mergeStaffField(updatedConsultation.pds, cachedConsultation.pds),
              })
            }
          })
          // for backward compatibility
          dispatch(updateConsultationSuccess(updatedConsultation))
        }
        try {
          await cacheDataLoaded
          socket.on(SocketEvent.CONSULTATION_UPDATED, consultationUpdatedListener)
          socket.emit(SocketEvent.ENTER_CONSULTATION, consultationId)
        } catch {}
        await cacheEntryRemoved
        socket.off(SocketEvent.CONSULTATION_UPDATED, consultationUpdatedListener)
        socket.emit(SocketEvent.LEAVE_CONSULTATION, consultationId)
      },
      providesTags: cacheByIdArg(Tag.CONSULTATION),
    }),
    getConsultationSettings: builder.query<{ automaticCloseHours: number }, void>({
      query: () => ({
        url: '/settings/consultation/auto-close',
        method: 'GET',
      }),
      providesTags: [Tag.CONSULTATION_SETTINGS],
    }),
    sendMesuresSocket: builder.mutation<void, { consultationId: string; mesures: Consultation['mesures'] }>({
      queryFn: async ({ consultationId, mesures }, _queryApi, _extraOptions, _baseQuery) => {
        try {
          const socket = await getSocket()
          socket.emit(SocketEvent.SEND_MESURES, { consultationId, mesures })
          return { data: undefined }
        } catch {
          return { error: { status: 'CUSTOM_ERROR', error: 'Failed to connect to socket' } }
        }
      },
      // Optionally invalidate the getConsultation cache for the affected consultation
      invalidatesTags: (result, error, { consultationId }) => [{ type: Tag.CONSULTATION, id: consultationId }],
    }),
    getConsultations: builder.query<[Consultation[], number], { query: any; options: SearchRequestOptions }>({
      query: ({ query, options }) => ({
        url: '/consultations/query',
        method: 'POST',
        body: { query, ...options },
      }),
    }),
    createConsultation: builder.mutation<Consultation, CreateConsultationParams>({
      query: (body) => ({
        url: '/consultations',
        method: 'POST',
        body,
      }),
    }),
    updateConsultation: builder.mutation<Consultation, WithId<{ changes: UpdateConsultationDto }>>({
      query: ({ id, changes }) => ({ url: `/consultations/${id}`, method: 'PATCH', body: changes }),
      async onQueryStarted({ id: consultationId }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedConsultation } = await queryFulfilled
          dispatch(
            consultationApi.util.updateQueryData('getConsultation', consultationId, (cachedConsultation) => {
              Object.assign(cachedConsultation, updatedConsultation)
            })
          )
        } catch {}
      },
    }),
    endConsultation: builder.mutation<Consultation, string>({
      query: (consultationId) => ({ url: `/consultations/${consultationId}/end`, method: 'PATCH' }),
      async onQueryStarted(consultationId, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedConsultation } = await queryFulfilled
          dispatch(
            consultationApi.util.updateQueryData('getConsultation', consultationId, (cachedConsultation) => {
              Object.assign(cachedConsultation, updatedConsultation)
            })
          )
        } catch {}
      },
    }),
    updateConsultationSettings: builder.mutation<void, { automaticCloseHours: number }>({
      query: (body) => ({
        url: '/settings/consultation/auto-close',
        method: 'PUT',
        body,
      }),
      invalidatesTags: [Tag.CONSULTATION_SETTINGS],
    }),
    createConsultationBiological: builder.mutation<Consultation, { consultationId: string; biological: CreateBiologicalParams }>({
      query: ({ consultationId, biological }) => ({
        url: `/consultations/${consultationId}/biologicals`,
        method: 'POST',
        body: biological,
      }),
    }),
    addConsultationBiologicalFile: builder.mutation<Consultation, { biologicalId: string; consultationId: string; file: File }>({
      async queryFn({ biologicalId, consultationId, file }, _queryApi, _extraOptions, baseQuery) {
        const presignedUrlResult = await fetchPresignedUrl(
          {
            fileName: file.name,
            fileType: file.type,
            objectType: 'consultation',
            objectId: consultationId,
          },
          baseQuery
        )
        if (presignedUrlResult.error) return { error: presignedUrlResult.error }

        const { url } = presignedUrlResult.data as { url: string }
        const uploadResult = await uploadToAWS(url, file)
        if (uploadResult.error) return { error: uploadResult.error }

        const addConsultationBiologicalFileResult = await baseQuery({
          url: `/consultations/${consultationId}/biologicals/${biologicalId}/files`,
          method: 'POST',
          body: {
            file: {
              filename: file.name,
              key: _.deburr(decodeURIComponent(new URL(url).pathname.slice(1))),
              objectType: 'consultation',
            },
          },
        })

        return addConsultationBiologicalFileResult.data
          ? { data: addConsultationBiologicalFileResult.data as Consultation }
          : { error: addConsultationBiologicalFileResult.error as FetchBaseQueryError }
      },
    }),
    createConsultationDictaphone: builder.mutation<Consultation, { consultationId: string; file: File }>({
      async queryFn({ consultationId, file }, _queryApi, _extraOptions, baseQuery) {
        const presignedUrlResult = await fetchPresignedUrl(
          {
            fileName: file.name,
            fileType: file.type,
            objectType: 'consultation',
            objectId: consultationId,
          },
          baseQuery
        )
        if (presignedUrlResult.error) return { error: presignedUrlResult.error }

        const { url } = presignedUrlResult.data as { url: string }
        const uploadResult = await uploadToAWS(url, file)
        if (uploadResult.error) return { error: uploadResult.error }

        const createDictaphoneResult = await baseQuery({
          url: `/consultations/${consultationId}/dictaphones`,
          method: 'POST',
          body: {
            file: {
              filename: file.name,
              key: _.deburr(decodeURIComponent(new URL(url).pathname.slice(1))),
              objectType: 'consultation',
            },
          },
        })

        return createDictaphoneResult.data
          ? { data: createDictaphoneResult.data as Consultation }
          : { error: createDictaphoneResult.error as FetchBaseQueryError }
      },
    }),
    deleteConsultationBiological: builder.mutation<Consultation, { biologicalId: string; consultationId: string }>({
      query: ({ biologicalId, consultationId }) => ({
        url: `/consultations/${consultationId}/biologicals/${biologicalId}`,
        method: 'DELETE',
      }),
    }),
    deleteConsultationBiologicalFiles: builder.mutation<
      Consultation,
      { biologicalId: string; consultationId: string; files: Omit<Document, 'objectType' | 'url'>[] }
    >({
      query: ({ biologicalId, consultationId, files }) => ({
        url: `/consultations/${consultationId}/biologicals/${biologicalId}/files/delete`,
        method: 'POST',
        body: { files },
      }),
    }),
    deleteConsultationDictaphone: builder.mutation<Consultation, { consultationId: string; filename: string; key: string }>({
      query: ({ consultationId, ...body }) => ({
        url: `/consultations/${consultationId}/dictaphones/delete`,
        method: 'POST',
        body,
      }),
    }),
    getConsultationReasons: builder.query<ConsultationReason[], SearchRequest>({
      query: (params) => ({ url: '/consultations/reasons', method: 'GET', params }),
      providesTags: [Tag.CONSULTATION_REASON],
    }),
    createConsultationReason: builder.mutation<ConsultationReason, { name: string }>({
      query: (body) => ({ url: '/consultations/reasons', method: 'POST', body }),
      invalidatesTags: [Tag.CONSULTATION_REASON],
    }),
    updateConsultationReason: builder.mutation<ConsultationReason, { id: string; name: string }>({
      query: ({ id, name }) => ({ url: `/consultations/reasons/${id}`, method: 'PATCH', body: { name } }),
      invalidatesTags: [Tag.CONSULTATION_REASON],
    }),
    deleteConsultationReason: builder.mutation<void, string>({
      query: (id) => ({ url: `/consultations/reasons/${id}`, method: 'DELETE' }),
      invalidatesTags: [Tag.CONSULTATION_REASON],
    }),
    addBiologicalDocumentMetadata: builder.mutation<Consultation, { documentDto: CreateDocumentDto; consultationId: string; biologicalId: string }>({
      query: ({ documentDto, consultationId, biologicalId }) => ({
        url: `/consultations/${consultationId}/biologicals/${biologicalId}/files`,
        method: 'POST',
        body: {
          file: {
            filename: documentDto.filename,
            key: documentDto.key,
            objectType: documentDto.objectType,
          },
        },
      }),
    }),
    retryDesmosActs: builder.mutation<Consultation, RetryDesmosActsParams>({
      query: ({ consultationId }) => ({
        url: `/consultations/${consultationId}/retry-desmos-acts`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { consultationId }) => [
        { type: Tag.CONSULTATION, id: consultationId },
        Tag.CONSULTATION, // Invalidate list queries too
      ],
    }),
    addManualDesmosAct: builder.mutation<Consultation, AddManualDesmosActParams>({
      query: ({ consultationId, actData }) => ({
        url: `/consultations/${consultationId}/manual-desmos-act`,
        method: 'POST',
        body: actData,
      }),
      invalidatesTags: (result, error, { consultationId }) => [
        { type: Tag.CONSULTATION, id: consultationId },
        Tag.CONSULTATION, // Invalidate list queries too
      ],
    }),
    updateBillingReview: builder.mutation<Consultation, WithId<{ changes: UpdateBillingReviewDto }>>({
      query: ({ id, changes }) => ({
        url: `/consultations/${id}/billing-review`,
        method: 'PATCH',
        body: changes,
      }),
      async onQueryStarted({ id: consultationId }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedConsultation } = await queryFulfilled
          dispatch(
            consultationApi.util.updateQueryData('getConsultation', consultationId, (cached) => {
              Object.assign(cached, updatedConsultation)
            })
          )
        } catch (err) {
          console.error('Failed to update billing review:', err)
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: Tag.CONSULTATION, id },
        Tag.CONSULTATION,
      ],
    }),
    setConsultationReadiness: builder.mutation<Consultation, WithId<{ changes: { ready: boolean; note?: string } }>>({
      query: ({ id, changes }) => ({
        url: `/consultations/${id}/readiness`,
        method: 'PATCH',
        body: changes,
      }),
      async onQueryStarted({ id: consultationId }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedConsultation } = await queryFulfilled
          dispatch(
            consultationApi.util.updateQueryData('getConsultation', consultationId, (cached) => {
              Object.assign(cached, updatedConsultation)
            })
          )
        } catch (err) {
          console.error('Failed to update consultation readiness:', err)
        }
      },
      invalidatesTags: (result, error, { id }) => [{ type: Tag.CONSULTATION, id }, Tag.CONSULTATION],
    }),
    getConsultationsByGroup: builder.query<Consultation[], string>({
      query: (groupId) => `/consultations/group/${groupId}`,
    }),
  }),
  overrideExisting: false,
})
